import React, { useMemo, useState } from 'react';
import styles from '../CanvassInfo.module.css';
import { Button } from '../../layout/buttons';
import { Modal } from '../../layout/Modal';
import { logger } from '../../../utils/logger';

/**
 * DetailsSections
 * Renders three sections: Audience Profile, Experiences, Behavioural Context
 * Provides a modal for editing a simple text value per section and saving via parent.
 *
 * Props:
 * - extractedInsights: object (with extractedInfo)
 * - groupedInsights: object (as computed in CanvassInfo)
 * - onSaveCanvassPatch: (patch: object) => Promise<void>
 */
export default function DetailsSections({ extractedInsights, groupedInsights, onSaveCanvassPatch, isEditable = true }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState(null); // 'audience' | 'experiences' | 'behavioural'
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const MAX_TEXT_LEN = 240;
  // Audience modal state
  const [audienceList, setAudienceList] = useState([]); // string[]
  const [newAudience, setNewAudience] = useState('');
  const [experienceList, setExperienceList] = useState([]); // string[]
  const [newExperience, setNewExperience] = useState('');

  const experienceItems = useMemo(() => {
    const tpItem = (extractedInsights?.extractedInfo || []).find((it) => it.key === 'touchpoints');
    if (!tpItem) return [];
    
    // tpItem.value should be the array of touchpoints
    const touchpointsArray = tpItem.value;
    
    if (!Array.isArray(touchpointsArray)) return [];
    
    // Each item in the array is a separate experience/touchpoint
    return touchpointsArray
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (typeof item === 'object' && item !== null) {
          // If it's an object, extract meaningful text
          return item.text || item.description || item.name || item.value || JSON.stringify(item);
        }
        return String(item).trim();
      })
      .filter(Boolean);
  }, [extractedInsights]);

  const audienceItems = useMemo(() => (
    (groupedInsights.audience_profile || []).filter((it) => it && it.key === 'audience')
  ), [groupedInsights]);

  const behaviouralKey = useMemo(() => (
    Array.isArray(groupedInsights.context)
    && groupedInsights.context.some((it) => it && it.key === 'behavioural_context')
      ? 'behavioural_context'
      : 'observed_behaviors'
  ), [groupedInsights]);

  const behaviouralItems = useMemo(() => (
    Array.isArray(groupedInsights.context)
      ? groupedInsights.context.filter((item) => item && item.key === behaviouralKey)
      : []
  ), [behaviouralKey, groupedInsights]);

  const openModal = (which) => {
    if (!isEditable) return;
    setField(which);
    if (which === 'audience') {
      // Build audience list from audience_profile items with key === 'audience'
      const items = (groupedInsights.audience_profile || [])
        .filter((it) => it && it.key === 'audience');
      const list = [];
      for (const it of items) {
        const v = it.value;
        if (Array.isArray(v)) {
          v.forEach((s) => {
            const t = String(s).trim();
            if (t) list.push(t);
          });
        } else if (typeof v === 'string') {
          v.split(',').map((s) => s.trim()).filter(Boolean).forEach((s) => list.push(s));
        }
      }
      setAudienceList(list);
      setNewAudience('');
      setText('');
    } else if (which === 'experiences') {
      setExperienceList(experienceItems);
      setNewExperience('');
      setText('');
    } else if (which === 'behavioural') {
      const joined = behaviouralItems
        .map((it) => (typeof it.value === 'string' ? it.value : ''))
        .filter(Boolean)
        .join('\n');
      setText(joined);
    }
    setOpen(true);
  };

  const onSave = async () => {
    if (!field) return;
    try {
      setSaving(true);
      const trimmed = (text || '').trim();
      if (field === 'audience') {
        // Save as a single comma-separated string
        const joined = audienceList.filter((s) => s.trim().length > 0).map((s) => s.trim()).join(', ');
        await onSaveCanvassPatch?.({ audience: joined });
      } else if (field === 'experiences') {
        const touchpoints = experienceList.filter((s) => s.trim().length > 0).map((s) => s.trim());
        await onSaveCanvassPatch?.({ touchpoints });
      } else if (field === 'behavioural') {
        // Store behavioural context in a flexible property supported by additionalProperties
        await onSaveCanvassPatch?.({ behavioural_context: trimmed });
      }
      setOpen(false);
    } catch (e) {
      logger.error('Failed to save canvass patch:', e);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Audience / Client Profile */}
      <section className={styles.section + ' ' + styles.sectionFirst}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Audience Profile</div>
          {isEditable && (
            <a className={styles.editLink} type="button" onClick={() => openModal('audience')}>Edit</a>
          )}
        </div>
        {audienceItems.length > 0 ? (
          <div className={styles.pills}>
            {audienceItems
              .flatMap((item, idx) => {
                const v = item.value;
                if (Array.isArray(v)) {
                  return v.map((tag, tIdx) => (
                    <span key={`${idx}-${tIdx}`} className={styles.pill}>{String(tag).trim()}</span>
                  ));
                }
                if (typeof v === 'string') {
                  return v.split(',').map((s, tIdx) => (
                    <span key={`${idx}-${tIdx}`} className={styles.pill} title={v}>
                      {s.trim()}
                    </span>
                  ));
                }
                return (
                  <span key={idx} className={styles.pill}>{String(v)}</span>
                );
              })}
          </div>
        ) : (
          <p className={styles.paragraph}>Audience Profile has yet to be added.</p>
        )}
        <div className={styles.divider} />
      </section>

      {/* Experiences */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Experiences</h4>
          {isEditable && (
            <a className={styles.editLink} type="button" onClick={() => openModal('experiences')}>Edit</a>
          )}
        </div>
        {experienceItems.length > 0 ? (
          <div className={styles.pills}>
            {experienceItems.map((item, idx) => (
              <span key={idx} className={styles.pill}>{item}</span>
            ))}
          </div>
        ) : (
          <div>
            <p className={styles.paragraph}>Experiences have yet to be added.</p>
          </div>
        )}
        <div className={styles.divider} />
      </section>

      {/* Behavioural Context */}
      <section className={styles.section + ' ' + styles.sectionLast}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Behavioural Context</h4>
          {isEditable && (
            <a className={styles.editLink} type="button" onClick={() => openModal('behavioural')}>Edit</a>
          )}
        </div>
        <div>
          {behaviouralItems.length > 0 ? (
            behaviouralItems.map((item, index) => (
              <p key={index} className={styles.paragraph}>{String(item.value)}</p>
            ))
          ) : (
            <p className={styles.paragraph}>Behavioural Context has yet to be added.</p>
          )}
        </div>
      </section>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={field === 'audience' ? 'Edit Audience Profile' : field === 'experiences' ? 'Edit Experiences' : 'Edit Behavioural Context'}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
            <Button variant="outline-neutral" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={!isEditable || saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        }
      >
        {field === 'audience' ? (
          <div>
            {/* Add new audience input */}
            <div className={styles.modalSectionTitleAudience}>
              <input
                type="text"
                value={newAudience}
                onChange={(e) => setNewAudience(e.target.value)}
                placeholder="Add audience (press Add to include)"
                disabled={!isEditable}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  const v = (newAudience || '').trim();
                  if (!v) return;
                  if (!audienceList.includes(v)) setAudienceList([...audienceList, v]);
                  setNewAudience('');
                }}
                disabled={!isEditable}
              >
                Add
              </Button>
              </div>
              <div className={styles.modalSeparator} />
              {/* Existing audiences as pills */}
              <div className={styles.sectionSubTitleAudience}>Audience Profile</div>
              <div className={styles.pills} style={{ marginBottom: 12 }}>
                {audienceList.map((a, i) => (
                  <span key={`${a}-${i}`} className={styles.pill}>
                    <span>{a}</span>
                    <button
                      type="button"
                      className={styles.pillClose}
                      aria-label={`Remove ${a}`}
                      onClick={() => {
                        setAudienceList(audienceList.filter((_, idx) => idx !== i));
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
          </div>
        ) : field === 'experiences' ? (
          <div>
            <div className={styles.modalSectionTitleAudience}>
              <input
                type="text"
                value={newExperience}
                onChange={(e) => setNewExperience(e.target.value)}
                placeholder="Add experience (press Add to include)"
                disabled={!isEditable}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  const v = (newExperience || '').trim();
                  if (!v) return;
                  if (!experienceList.includes(v)) setExperienceList([...experienceList, v]);
                  setNewExperience('');
                }}
                disabled={!isEditable}
              >
                Add
              </Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {experienceList.map((item, idx) => (
                <span key={`${item}-${idx}`} className={styles.pill}>
                  {item}
                  {isEditable && (
                    <button
                      type="button"
                      onClick={() => setExperienceList(experienceList.filter((_, i) => i !== idx))}
                      style={{ marginLeft: 8, border: 'none', background: 'transparent', cursor: 'pointer' }}
                      aria-label={`Remove ${item}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={field === 'experiences' ? 'List touchpoints separated by commas or semicolons' : 'Enter behavioural context'}
              rows={6}
              maxLength={MAX_TEXT_LEN}
              readOnly={!isEditable}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', background: !isEditable ? '#f8f9fa' : '#fff' }}
            />
            <div className={styles.pushRight}>
              <div className={`${styles.inputSubLabel} ${text.length >= MAX_TEXT_LEN ? styles.charCounterMax : styles.charCounterOk}`}>
                {text.length} / {MAX_TEXT_LEN} Characters
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
